/**
 * useKanbanDragDrop Hook
 *
 * Manages all drag-and-drop state and handlers for the Kanban board.
 * Handles both column reordering and card movement between columns.
 */

import { adjustColumnOverlay, adjustCardForSidebar } from '../../utils';
import useKanbanDragState from './useKanbanDragState';
import useDragStartHandler from './useDragStartHandler';
import useDragOverHandler from './useDragOverHandler';
import useDragEndHandler from './useDragEndHandler';
import useDragMoveHandler from './useDragMoveHandler';

const useKanbanDragDrop = ({
  columns,
  columnIds,
  taskIdsByColumn,
  tasksByStatus,
  tasks,
  statuses,
  onStatusChange,
  onSetStatusAutoSync,
  onColumnReorder,
  onWipLimitBlocked,
  boardRef,
  hasSwimlanes = false,
  swimlanes = null,
  campaignId = '',
}) => {
  const dragState = useKanbanDragState({
    columnIds,
    taskIdsByColumn,
    tasksByStatus,
    boardRef,
  });

  const handleDragStart = useDragStartHandler({
    columnIds,
    tasksByStatus,
    tasks,
    campaignId,
    hasSwimlanes,
    swimlanes,
    saveScrollPositions: dragState.saveScrollPositions,
    doLockPageScroll: dragState.doLockPageScroll,
    setActiveColumnId: dragState.setActiveColumnId,
    setActiveCardId: dragState.setActiveCardId,
    setActiveCardSwimlaneId: dragState.setActiveCardSwimlaneId,
    setVisibleCardCount: dragState.setVisibleCardCount,
    setFrozenTaskCounts: dragState.setFrozenTaskCounts,
    setFrozenHiddenCounts: dragState.setFrozenHiddenCounts,
    lastStatusChangeRef: dragState.lastStatusChangeRef,
  });

  const handleDragOver = useDragOverHandler({
    activeCardId: dragState.activeCardId,
    columnIds,
    columns,
    taskIdsByColumn,
    tasksByStatus,
    statuses,
    findColumn: dragState.findColumn,
    getPositionIndex: dragState.getPositionIndex,
    dropIndexRef: dragState.dropIndexRef,
    dropColumnRef: dragState.dropColumnRef,
    lastStatusChangeRef: dragState.lastStatusChangeRef,
    onStatusChange,
    onSetStatusAutoSync,
    onColumnReorder,
    onWipLimitBlocked,
  });

  const handleDragEnd = useDragEndHandler({
    columnIds,
    columns,
    tasksByStatus,
    statuses,
    findColumn: dragState.findColumn,
    getPositionIndex: dragState.getPositionIndex,
    dropIndexRef: dragState.dropIndexRef,
    dropColumnRef: dragState.dropColumnRef,
    lastStatusChangeRef: dragState.lastStatusChangeRef,
    unlockScrollAndRestore: dragState.unlockScrollAndRestore,
    resetDragState: dragState.resetDragState,
    onColumnReorder,
    onStatusChange,
    onSetStatusAutoSync,
    onWipLimitBlocked,
  });

  const handleDragMove = useDragMoveHandler();

  return {
    activeColumnId: dragState.activeColumnId,
    activeCardId: dragState.activeCardId,
    activeCardSwimlaneId: dragState.activeCardSwimlaneId,
    frozenTaskCounts: dragState.frozenTaskCounts,
    frozenHiddenCounts: dragState.frozenHiddenCounts,
    visibleCardCount: dragState.visibleCardCount,
    sensors: dragState.sensors,
    collisionDetection: dragState.collisionDetection,
    autoScrollConfig: dragState.autoScrollConfig,
    handleDragStart,
    handleDragMove,
    handleDragOver,
    handleDragEnd,
    adjustColumnOverlay,
    adjustCardForSidebar,
  };
};

export default useKanbanDragDrop;
